// Queues, triage, vitals and allergies (docs/plans/phase1-triage.md §3.3).
import type { replaceVitalRulesRequestSchema, triageCategoryInputSchema } from '@wodi/shared';
import {
  clinicalRulesSchema,
  queueBoardSchema,
  servicePointQueueSchema,
  transferResultSchema,
  triageCategorySchema,
  triageViewSchema,
  type RecordAllergyRequest,
  type RecordTriageRequest,
  type TriageSettingsView,
} from '@wodi/shared';
import { queryOptions } from '@tanstack/react-query';
import type { VitalKey } from '@wodi/domain';
import { z } from 'zod';

import { api } from './client.ts';

/** How often queue screens refresh while visible (plan P3). */
export const QUEUE_REFRESH_MS = 5_000;

export const queuesQuery = queryOptions({
  queryKey: ['queues'],
  queryFn: () => api('GET', '/queues', { schema: z.array(servicePointQueueSchema) }),
  refetchInterval: QUEUE_REFRESH_MS,
});

export const queueBoardQuery = (servicePointId: string) =>
  queryOptions({
    queryKey: ['queues', servicePointId, 'entries'],
    queryFn: () => api('GET', `/queues/${servicePointId}/entries`, { schema: queueBoardSchema }),
    refetchInterval: QUEUE_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

const entryResult = z.object({ entryId: z.uuid() });

export function callNext(servicePointId: string) {
  return api('POST', `/queues/${servicePointId}/call-next`, { schema: entryResult });
}

export type QueueStep = 'call' | 'start' | 'return' | 'finish';

export function queueStep(entryId: string, step: QueueStep) {
  return api('POST', `/queue-entries/${entryId}/${step}`, { schema: entryResult });
}

export function removeFromQueue(entryId: string, reason: string) {
  return api('POST', `/queue-entries/${entryId}/cancel`, {
    body: { reason },
    schema: entryResult,
  });
}

export const priorityFlagsQuery = queryOptions({
  queryKey: ['queue-priority-flags'],
  queryFn: () =>
    api('GET', '/queue-priority-flags', {
      schema: z.array(z.object({ code: z.string(), display: z.string() })),
    }),
  staleTime: 60_000,
});

export function setQueueFlags(entryId: string, flags: string[]) {
  return api('PUT', `/queue-entries/${entryId}/flags`, { body: { flags } });
}

export function transferPatient(entryId: string, toServicePointId: string, note?: string) {
  return api('POST', `/queue-entries/${entryId}/transfer`, {
    body: { toServicePointId, ...(note === undefined || note === '' ? {} : { note }) },
    schema: transferResultSchema,
  });
}

export const clinicalRulesQuery = queryOptions({
  queryKey: ['clinical-rules'],
  queryFn: () => api('GET', '/clinical-rules', { schema: clinicalRulesSchema }),
  staleTime: 60_000,
});

export const triageQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['visits', visitId, 'triage'],
    queryFn: () => api('GET', `/visits/${visitId}/triage`, { schema: triageViewSchema }),
  });

export function recordTriage(visitId: string, body: RecordTriageRequest) {
  return api('POST', `/visits/${visitId}/triage`, { body, schema: triageViewSchema });
}

export function recordAllergy(visitId: string, body: RecordAllergyRequest) {
  return api('POST', `/visits/${visitId}/allergies`, { body, schema: triageViewSchema });
}

export function recordNoKnownAllergies(visitId: string) {
  return api('POST', `/visits/${visitId}/allergies/no-known`, { schema: triageViewSchema });
}

export function correctObservation(observationId: string, value: string, reason: string) {
  return api('POST', `/observations/${observationId}/correct`, {
    body: { value, reason, acknowledgements: [] },
  });
}

export type EnteredInErrorKind =
  'observations' | 'vital-sign-sets' | 'triage-assessments' | 'allergies' | 'allergy-assertions';

export function enterInError(kind: EnteredInErrorKind, id: string, reason: string) {
  return api('POST', `/${kind}/${id}/entered-in-error`, { body: { reason } });
}

// --- Settings > Clinical rules (triage plan §6.4) -------------------------------------------

export function replaceVitalRules(
  vitalKey: VitalKey,
  body: z.input<typeof replaceVitalRulesRequestSchema>,
) {
  return api('PUT', `/admin/clinical-rules/vitals/${vitalKey}`, {
    body,
    schema: clinicalRulesSchema,
  });
}

export function approveVitalRule(ruleId: string) {
  return api('POST', `/admin/clinical-rules/${ruleId}/approve`, { schema: clinicalRulesSchema });
}

export const adminTriageCategoriesQuery = queryOptions({
  queryKey: ['admin', 'triage-categories'],
  queryFn: () => api('GET', '/admin/triage-categories', { schema: z.array(triageCategorySchema) }),
});

export function saveTriageCategory(body: z.input<typeof triageCategoryInputSchema>) {
  return api('PUT', '/admin/triage-categories', { body, schema: clinicalRulesSchema });
}

export function approveTriageCategory(id: string) {
  return api('POST', `/admin/triage-categories/${id}/approve`, { schema: clinicalRulesSchema });
}

export function setVitalCoding(vitalKey: VitalKey, codeSystemKey: string, code: string) {
  return api('PUT', `/admin/vital-codings/${vitalKey}`, {
    body: { codeSystemKey, code },
    schema: clinicalRulesSchema,
  });
}

export function saveTriageSettings(body: TriageSettingsView) {
  return api('PUT', '/admin/triage-settings', { body, schema: clinicalRulesSchema });
}
